import { studentAPI } from './api';

/**
 * Where to send a student once they are authenticated.
 *
 * Login and the root route both hardcoded `/dashboard` — the ESSLCE dashboard
 * — so a student who bought UAT landed on a different exam every time they
 * opened the app. Fixing the catalog button only fixed the once-only path;
 * this is the one they take every morning.
 *
 * The decision is made SERVER-SIDE (see entitlements.home_for_student) and
 * arrives on the profile, so there is one rule rather than four copies of a
 * branch on program code. Students without a package entitlement — nearly all
 * 26,234 of them — resolve to `/dashboard` exactly as before.
 *
 * Falls back to `/dashboard` on any failure: a routing hint must never be the
 * reason someone cannot get into the app.
 */
/** Set by the public UAT band before it sends a visitor to sign in. */
const INTENT_KEY = 'lomi_intent_package';

export async function homeRoute(): Promise<string> {
  // Someone who arrived FOR a package must land in it. The server cannot know
  // this yet — they have no entitlement, so `student.home` is the ESLCE
  // dashboard, which is a different exam from the one the ad promised.
  //
  // Consumed on first use: the intent describes how they arrived, not where
  // they live, so it must not keep redirecting them tomorrow.
  try {
    const intent = localStorage.getItem(INTENT_KEY);
    if (intent) {
      localStorage.removeItem(INTENT_KEY);
      // The storefront, where the card is waiting to be tapped. Deliberately
      // not a route that auto-enrols: a student should choose the package
      // they are joining, and this is one tap on a page that already exists
      // rather than a new route that would need its own gate and error state.
      return '/packages';
    }
  } catch {
    /* storage blocked — fall through to the server's answer */
  }

  try {
    const profile = await studentAPI.getProfile();
    return profile?.student?.home || '/dashboard';
  } catch {
    return '/dashboard';
  }
}
